import { useState, useEffect, useRef } from "react";
import { API, useAuth } from "../App";
import axios from "axios";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { MessageCircle, Send, CheckSquare, ListTodo, Square, Lock, Sparkles } from "lucide-react";

const AdvisorChat = () => {
  const { user } = useAuth();
  const isFreeUser = user?.user_tier === 'sin_asesoria';
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [isTask, setIsTask] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const messagesEndRef = useRef(null);
  const shouldScroll = useRef(false);

  useEffect(() => {
    if (!isFreeUser) fetchMessages();
  }, [isFreeUser]);

  useEffect(() => {
    if (shouldScroll.current) {
      shouldScroll.current = false;
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [messages]);

  if (isFreeUser) {
    return (
      <Card className="bg-[#1a2332] border-[#2a3444]" data-testid="advisor-chat-locked">
        <CardContent className="p-6 text-center">
          <Lock className="w-10 h-10 text-gray-600 mx-auto mb-3" />
          <h3 className="text-white font-semibold mb-2">Chat con Asesor</h3>
          <p className="text-gray-400 text-sm mb-4">
            Comunícate directamente con tu asesor financiero. Disponible para usuarios con asesoría.
          </p>
          <a
            href="https://wa.me/573177848600?text=Hola%2C%20me%20interesa%20la%20asesor%C3%ADa%20financiera"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#0a0e1a] px-5 py-2 rounded-full font-semibold text-sm hover:bg-[#e8c84a] transition-all"
            data-testid="upgrade-to-advisory-chat"
          >
            <Sparkles size={16} />
            Solicitar Asesoría
          </a>
        </CardContent>
      </Card>
    );
  }

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/messages`, { withCredentials: true });
      setMessages(response.data);
    } catch (error) {
      console.error("Error fetching messages:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    try {
      await axios.post(`${API}/messages`, {
        content: newMessage,
        is_task: isTask
      }, { withCredentials: true });
      setNewMessage("");
      setIsTask(false);
      shouldScroll.current = true;
      fetchMessages();
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  const toggleComplete = async (messageId) => {
    try {
      await axios.put(`${API}/messages/${messageId}/complete`, {}, { withCredentials: true });
      fetchMessages();
    } catch (error) {
      console.error("Error toggling:", error);
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-CO', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  const filteredMessages = messages.filter(msg => {
    if (filter === "pending") return msg.is_task && !msg.is_completed;
    if (filter === "completed") return msg.is_task && msg.is_completed;
    return true;
  });

  const pendingTasks = messages.filter(m => m.is_task && !m.is_completed).length;

  return (
    <Card className="bg-[#1a2332] border-[#2a3444]" data-testid="advisor-chat">
      <CardHeader className="pb-3">
        <CardTitle className="text-white flex items-center gap-2" style={{ fontFamily: 'Playfair Display, serif' }}>
          <MessageCircle className="w-5 h-5 text-[#D4AF37]" />
          Chat con Asesor
        </CardTitle>
        <div className="flex items-center gap-2 mt-2">
          {[
            { id: "all", label: "Todo" },
            { id: "pending", label: `Pendientes${pendingTasks > 0 ? ` (${pendingTasks})` : ''}` },
            { id: "completed", label: "Completados" }
          ].map(f => (
            <button key={f.id} onClick={() => setFilter(f.id)}
              className={`text-[10px] px-2 py-1 rounded-md transition-colors ${
                filter === f.id ? 'bg-[#D4AF37] text-[#141b2d] font-medium' : 'text-gray-500 hover:text-gray-300'
              }`} data-testid={`filter-${f.id}`}>
              {f.label}
            </button>
          ))}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Messages area */}
        <div className="max-h-[350px] overflow-y-auto space-y-2 pr-1" data-testid="messages-list">
          {loading ? (
            <div className="text-center text-gray-500 py-4 animate-pulse">Cargando...</div>
          ) : filteredMessages.length > 0 ? (
            filteredMessages.map((msg) => (
              <div key={msg.message_id}
                className={`p-3 rounded-lg ${
                  msg.sender_role === 'admin' 
                    ? 'bg-[#D4AF37]/10 border border-[#D4AF37]/20 ml-0 mr-4' 
                    : 'bg-[#141b2d] border border-[#2a3444] ml-4 mr-0'
                }`}
                data-testid={`message-${msg.message_id}`}
              >
                <div className="flex items-start gap-2">
                  {msg.is_task && (
                    <button onClick={() => toggleComplete(msg.message_id)} className="mt-0.5 shrink-0"
                      data-testid={`task-toggle-${msg.message_id}`}>
                      {msg.is_completed 
                        ? <CheckSquare className="w-4 h-4 text-green-500" />
                        : <Square className="w-4 h-4 text-gray-500 hover:text-[#D4AF37]" />
                      }
                    </button>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs font-medium ${
                        msg.sender_role === 'admin' ? 'text-[#D4AF37]' : 'text-gray-400'
                      }`}>
                        {msg.sender_role === 'admin' ? 'Asesor' : 'Tu'}
                      </span>
                      {msg.is_task && (
                        <span className={`text-[9px] px-1.5 py-0.5 rounded ${
                          msg.is_completed ? 'bg-green-500/20 text-green-400' : 'bg-[#D4AF37]/20 text-[#D4AF37]'
                        }`}>{msg.is_completed ? 'Completada' : 'Tarea'}</span>
                      )}
                      <span className="text-[10px] text-gray-600">{formatTime(msg.created_at)}</span>
                    </div>
                    <p className={`text-sm ${
                      msg.is_completed ? 'line-through text-gray-600' : 'text-gray-300'
                    }`}>
                      {msg.content}
                    </p>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-6">
              <MessageCircle className="w-8 h-8 mx-auto mb-2 opacity-30 text-gray-600" />
              <p className="text-sm text-gray-600">
                {filter === "all" ? "No hay mensajes aún" : filter === "pending" ? "No hay tareas pendientes" : "No hay tareas completadas"}
              </p>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <form onSubmit={handleSend} className="flex items-center gap-2 pt-2 border-t border-[#2a3444]">
          <button type="button" onClick={() => setIsTask(!isTask)}
            className={`p-2 rounded-md transition-all shrink-0 ${
              isTask ? 'bg-[#D4AF37]/20 text-[#D4AF37]' : 'text-gray-500 hover:text-gray-300'
            }`}
            title={isTask ? "Enviando como tarea" : "Enviar como tarea"}
            data-testid="toggle-task-btn">
            <ListTodo className="w-4 h-4" />
          </button>
          <Input placeholder={isTask ? "Escribe una tarea..." : "Escribe un mensaje..."}
            className="flex-1 bg-[#141b2d] border-[#2a3444] text-white text-sm"
            value={newMessage} onChange={(e) => setNewMessage(e.target.value)}
            data-testid="chat-input" />
          <Button type="submit" size="icon" className="btn-gold shrink-0" disabled={!newMessage.trim()}
            data-testid="send-message-btn">
            <Send className="w-4 h-4" />
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default AdvisorChat;
